import getSiteConfig from '../data/siteConfig';
import styled from 'styled-components';
import ErrorLayout from '../components/Layout/ErrorLayout';
import {Block} from '@components/Layout';
import Stretch from '../components/Layout/Stretch';
import Page from '../components/Page';
import {Footer} from '../components/FooterComponent';
import AnnouncementBar from '@components/AnnouncementBarComponent/mapSiteConfig';
import {InteriorHero} from '@components/InteriorHero';
import Navigation from '@components/Navigation/mapSiteConfig';
import {SiteConfig} from '@data/types';
import {color, font, query} from '@design/templateFns';
import {useState} from 'react';

import * as y from 'yup';
import contactInquiries from '../data/contactInquiries.json';
import sendEmail from '../services/sendEmail';

export const getStaticProps = async ({params}: {params: {slug: string}}) => {
  const siteConfig = await getSiteConfig();
  return {
    props: {
      siteConfig: siteConfig,
    },
  };
};

const Space = styled.div`
  @media screen and (${query.below('desktop')}) {
    padding-top: 77px;
  }
`;

const OverWrite = styled.div`
  > div {
    background-color: ${color('bg')};
  }
`;

const StyledForm = styled.form`
  overflow: inherit;
  margin-top: 90px;

  @media (${query.atLeast('desktop')}) {
    max-width: 1000px;
    margin: auto;
  }
`;

const StyledInputContainer = styled.div`
  margin-bottom: 40px;
`;

const StyledInput = styled.input`
  height: 45px;
  font-size: 16px;
  ${font('body-sm-regular')};
  outline: 0px;
  padding: 0px;
  border-width: 1px;
  border-style: solid;
  border-color: transparent transparent rgb(0, 0, 0);
  border-image: initial;
  color: black;
  display: block;
  width: 100%;
  margin-bottom: 8px;
`;

const StyledSelect = styled.select`
  ${font('body-sm-medium')};
  height: 45px;
  font-size: 16px;
  outline: 0px;
  padding: 0px;
  border-width: 1px;
  border-style: solid;
  border-color: transparent transparent rgb(0, 0, 0);
  border-image: initial;
  color: black;
  display: block;
  width: 100%;
  margin-bottom: 8px;
`;

const StyledTextarea = styled.textarea`
  ${font('body-sm-regular')};
  height: 200px;
  font-size: 16px;
  outline: 0px;
  border: 1px solid rgb(0, 0, 0);
  color: black;
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  margin-bottom: 8px;

  &::placeholder {
    padding: 10px;
  }
`;

const StyledSubmit = styled(StyledInput)`
  border: 1px solid;
  border-radius: 41px;
  cursor: pointer;
  background-color: ${color('cta')};
  color: ${color('bg')};
  border-color: ${color('cta')};
  max-width: 200px;
  margin: auto;
`;

const StyledLabel = styled.label`
  ${font('body-sm-medium')};
`;
const StyledError = styled(StyledLabel)`
  color: red;
`;

const StyledLabelCenter = styled(StyledLabel)`
  text-align: center;
  display: block;
  font-size: 16px;
  margin-bottom: 40px;
`;

const ContactPage = (props) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [inquiry, setInquiry] = useState('');
  const [businessLocation, setBusinessLocation] = useState('');
  const [formErrors, setFormErrors] = useState({} as any);

  const formSchema = y.object().shape({
    name: y.string().required('Please provide your name'),
    email: y
      .string()
      .required('Please provide your email')
      .email('Please provide your correct email!'),
    message: y.string().required('Please provide your message'),
    inquiry: y.string().required('Please provide your inquiry'),
  });

  const validateFormAndSubmit = () => {
    formSchema
      .validate(
        {
          name,
          email,
          message,
          inquiry,
          businessLocation,
        },
        {abortEarly: false},
      )
      .then((res) =>
        sendEmail({
          ...res,
          emailTo: contactInquiries.find(
            (contactInquiry) => contactInquiry.value === res.inquiry,
          ).email,
        }).then((response) => response.success),
      )
      .catch((err) => {
        const errorsObj = {};
        err.inner.forEach(({path, message}) => {
          errorsObj[path] = message;
        });
        setFormErrors(errorsObj);
      });
  };

  return (
    <Page
      description="Contact us for media inquiries and information about our products and services. Our dedicated team is here to provide you with support and assistance"
      metatitle="Contact Koppers Inc."
    >
      <OverWrite>
        <ErrorLayout>
          <AnnouncementBar {...(props.siteConfig as SiteConfig)} />
          <Navigation {...(props.siteConfig as SiteConfig)} />
          <Space />
          <Stretch />
          <Block narrow>
            <InteriorHero
              header="Contact Koppers"
              caption="Koppers is a leading provider of wood- and carbon-based technologies, products, chemicals, and services that strengthen our global infrastructure. Contact us about any of our offerings today."
            />
            <StyledForm action="/thank-you">
              <StyledLabelCenter>
                In addition to your primary contact information, please provide
                the reason for your inquiry from the menu below.
              </StyledLabelCenter>
              <StyledInputContainer>
                <StyledSelect
                  id="inquiry"
                  name="inquiry"
                  onChange={(e) => setInquiry(e.target.value)}
                  value={inquiry}
                  required
                >
                  <option value="">Choose One</option>
                  {contactInquiries.map((contactInquiry) => (
                    <option
                      key={contactInquiry.value}
                      value={contactInquiry.value}
                    >
                      {contactInquiry.name}
                    </option>
                  ))}
                </StyledSelect>
                <StyledLabel>Select Inquiry*</StyledLabel>
                {formErrors && (
                  <div>
                    <StyledError>{formErrors.inquiry}</StyledError>
                  </div>
                )}
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your Name"
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                  required
                ></StyledInput>
                <StyledLabel>Your Name*</StyledLabel>
                {formErrors && (
                  <div>
                    <StyledError>{formErrors.name}</StyledError>
                  </div>
                )}
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  id="email"
                  name="email"
                  placeholder="Your Email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  required
                ></StyledInput>
                <StyledLabel>Your Email*</StyledLabel>
                {formErrors && (
                  <div>
                    <StyledError>{formErrors.email}</StyledError>
                  </div>
                )}
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledInput
                  type="text"
                  id="businessLocation"
                  name="businessLocation"
                  placeholder="City, State/Province and Country"
                  onChange={(e) => setBusinessLocation(e.target.value)}
                  value={businessLocation}
                  required
                ></StyledInput>
                <StyledLabel>Your Business Location*</StyledLabel>
                {formErrors && (
                  <div>
                    <StyledError>{formErrors.message}</StyledError>
                  </div>
                )}
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledTextarea
                  type="textarea"
                  id="message"
                  name="message"
                  placeholder="Your Message"
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                  required
                ></StyledTextarea>
                <StyledLabel>Your Message* </StyledLabel>
                {formErrors && (
                  <div>
                    <StyledError>{formErrors.message}</StyledError>
                  </div>
                )}
              </StyledInputContainer>
              <StyledInputContainer>
                <StyledSubmit
                  onClick={formSchema.isValid ? validateFormAndSubmit : null}
                  type="submit"
                  value="Submit"
                ></StyledSubmit>
              </StyledInputContainer>
            </StyledForm>
          </Block>
          <Footer siteConfig={props.siteConfig as SiteConfig} />
        </ErrorLayout>
      </OverWrite>
    </Page>
  );
};

export default ContactPage;
